/* filepath: /Users/george/MatrizInc/MarketBrewer/Clients/street-lawyer-services/website/src/core/Pages/ProductsPage/ProductCategories/index.tsx */
import React, { useRef, useEffect, useState } from "react";
import { productCategories } from "../data";
import {
  animateTitle,
  animateCategories,
  cleanupScrollTriggers,
  fadeInImage,
} from "./gsap";

const ProductCategories: React.FC = () => {
  const sectionRef = useRef<HTMLDivElement>(null);
  const titleRef = useRef<HTMLHeadingElement>(null);
  const categoryRefs = useRef<(HTMLDivElement | null)[]>([]);
  const imageRefs = useRef<(HTMLImageElement | null)[]>([]);
  const [imagesLoaded, setImagesLoaded] = useState<Record<string, boolean>>({});
  const [imageErrors, setImageErrors] = useState<Record<string, boolean>>({});

  // Initialize image loading states
  useEffect(() => {
    const initialLoadingState: Record<string, boolean> = {};
    const initialErrorState: Record<string, boolean> = {};

    productCategories.forEach((category) => {
      initialLoadingState[category.id] = false;
      initialErrorState[category.id] = false;
    });

    setImagesLoaded(initialLoadingState);
    setImageErrors(initialErrorState);

    // Initialize refs array with correct length
    categoryRefs.current = new Array(productCategories.length).fill(null);
    imageRefs.current = new Array(productCategories.length).fill(null);
  }, []);

  // Handle GSAP animations
  useEffect(() => {
    const animationTimeout = setTimeout(() => {
      if (sectionRef.current && titleRef.current) {
        const triggers = [];

        // Animate title
        const titleTrigger = animateTitle(
          { current: titleRef.current },
          { current: sectionRef.current }
        );
        if (titleTrigger) triggers.push(titleTrigger);

        // Animate categories
        const categoryTriggers = animateCategories(
          { current: categoryRefs.current },
          { current: sectionRef.current },
          imagesLoaded
        );
        triggers.push(...categoryTriggers);
      }
    }, 100);

    return () => {
      clearTimeout(animationTimeout);
      cleanupScrollTriggers();
    };
  }, [imagesLoaded]); // Re-run when images load state changes

  // Handle image load events
  const handleImageLoad = (
    categoryId: string,
    index: number,
    img: HTMLImageElement
  ) => {
    console.log(`✅ Image loaded successfully for category: ${categoryId}`);

    // Store reference to the image
    imageRefs.current[index] = img;

    // Update loaded state
    setImagesLoaded((prev) => ({
      ...prev,
      [categoryId]: true,
    }));

    // Apply fade-in animation using GSAP
    fadeInImage(img, index * 0.1);
  };

  const handleImageError = (categoryId: string, src: string) => {
    console.error(`❌ Failed to load image for category ${categoryId}: ${src}`);

    setImageErrors((prev) => ({
      ...prev,
      [categoryId]: true,
    }));
  };

  // Helper to check if an image is a GIF
  const isGif = (path: string): boolean => {
    return path.toLowerCase().endsWith(".gif");
  };

  return (
    <div ref={sectionRef} id="product-categories" className="py-16 bg-white">
      <div className="container mx-auto px-4">
        <h2
          ref={titleRef}
          className="text-3xl md:text-4xl font-bold text-center mb-4"
        >
          Our Product Categories
        </h2>
        <p className="text-lg text-center text-gray-600 mb-12 max-w-3xl mx-auto">
          Explore our diverse selection of premium cannabis products, each
          carefully selected for quality, potency, and exceptional experience.
        </p>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-8 lg:gap-12">
          {productCategories.map((category, index) => (
            <div
              key={category.id}
              ref={(el) => (categoryRefs.current[index] = el)}
              data-category-id={category.id}
              className="bg-gray-50 rounded-xl overflow-hidden shadow-lg flex flex-col hover:shadow-xl transition-shadow duration-300"
            >
              <div className="relative h-64 overflow-hidden">
                {/* Loading skeleton */}
                {!imagesLoaded[category.id] && !imageErrors[category.id] && (
                  <div className="absolute inset-0 bg-gray-200 animate-pulse flex items-center justify-center">
                    <svg
                      className="w-12 h-12 text-gray-400"
                      xmlns="http://www.w3.org/2000/svg"
                      fill="none"
                      viewBox="0 0 24 24"
                    >
                      <circle
                        className="opacity-25"
                        cx="12"
                        cy="12"
                        r="10"
                        stroke="currentColor"
                        strokeWidth="4"
                      ></circle>
                      <path
                        className="opacity-75"
                        fill="currentColor"
                        d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                      ></path>
                    </svg>
                  </div>
                )}

                {/* Error fallback */}
                {imageErrors[category.id] && (
                  <div className="absolute inset-0 flex flex-col items-center justify-center bg-gray-300 p-4">
                    <svg
                      className="w-12 h-12 text-gray-500 mb-2"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth="2"
                        d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
                      />
                    </svg>
                    <p className="font-medium text-gray-700">
                      {category.title}
                    </p>
                  </div>
                )}

                {/* Image with WebP support */}
                {!imageErrors[category.id] &&
                  (isGif(category.image) ? (
                    <img
                      src={category.image}
                      alt={category.title}
                      ref={(el) => el && (imageRefs.current[index] = el)}
                      className="w-full h-full object-cover transition-transform duration-500 hover:scale-105"
                      style={{
                        opacity: 0,
                      }} /* Start with opacity 0 for fade in */
                      loading="lazy"
                      fetchPriority={
                        index < 2 ? "high" : "auto"
                      } /* Changed from fetchpriority to fetchPriority */
                      decoding="async"
                      onLoad={(e) =>
                        handleImageLoad(
                          category.id,
                          index,
                          e.target as HTMLImageElement
                        )
                      }
                      onError={() =>
                        handleImageError(category.id, category.image)
                      }
                    />
                  ) : category.imageWebp ? (
                    <picture>
                      <source srcSet={category.imageWebp} type="image/webp" />
                      <img
                        src={category.image}
                        alt={category.title}
                        ref={(el) => el && (imageRefs.current[index] = el)}
                        className="w-full h-full object-cover transition-transform duration-500 hover:scale-105"
                        style={{
                          opacity: 0,
                        }} /* Start with opacity 0 for fade in */
                        loading="lazy"
                        fetchPriority={
                          index < 2 ? "high" : "auto"
                        } /* Changed from fetchpriority to fetchPriority */
                        decoding="async"
                        onLoad={(e) =>
                          handleImageLoad(
                            category.id,
                            index,
                            e.target as HTMLImageElement
                          )
                        }
                        onError={() =>
                          handleImageError(category.id, category.image)
                        }
                      />
                    </picture>
                  ) : (
                    <img
                      src={category.image}
                      alt={category.title}
                      ref={(el) => el && (imageRefs.current[index] = el)}
                      className="w-full h-full object-cover transition-transform duration-500 hover:scale-105"
                      style={{
                        opacity: 0,
                      }} /* Start with opacity 0 for fade in */
                      loading="lazy"
                      fetchPriority={
                        index < 2 ? "high" : "auto"
                      } /* Changed from fetchpriority to fetchPriority */
                      decoding="async"
                      onLoad={(e) =>
                        handleImageLoad(
                          category.id,
                          index,
                          e.target as HTMLImageElement
                        )
                      }
                      onError={() =>
                        handleImageError(category.id, category.image)
                      }
                    />
                  ))}

                <div className="absolute inset-0 bg-gradient-to-t from-black/70 to-transparent"></div>
                <h3 className="absolute bottom-0 left-0 right-0 text-white text-2xl font-bold p-6">
                  {category.title}
                </h3>
              </div>

              <div className="p-6 flex-grow">
                <p className="text-gray-700">{category.description}</p>
              </div>
            </div>
          ))}
        </div>

        {/* Hidden test image for path validation - helps debug 404 issues */}
        <div className="hidden" aria-hidden="true">
          <img
            src="/images/kief-infused-joints-inside-dispensary.jpg"
            alt="Test image"
            onLoad={() => console.log("✅ Test image loaded successfully")}
            onError={() => console.error("❌ Test image failed to load")}
          />
        </div>
      </div>
    </div>
  );
};

export default ProductCategories;
